import { useMemo, useState, useRef } from "react";
import { ChartWrapper } from "../../../../components/ChartWrapper";
import { useProductTrendingData } from "../MarketLandscape/hooks/useTrendingProductData";
import { convertProductTimeSeriesData } from "../MarketLandscape/utils";
import { getTop5Products } from "./utils";
import useMatchProductsById from "../MarketLandscape/hooks/useMatchProductsById";
import { BaseDateDataChart } from "../../../../components/charts/BaseDateDataChart/BaseDateDataChart";
import { PeriodSelect } from "../../../../components/filters/PeriodSelect";
import { LineChartColors } from "../../../../components/charts/constants";
import { useWorkspaceStore } from "../../../../store/workspace/WorkspaceStore";
import { determineChartContent, hasNoData } from "../../utils";
import { useCombinedFilters } from "../../../../hooks/useCombinedFilters";
import * as am4charts from "@amcharts/amcharts4/charts";
import { CHART_CONSTANTS } from "../constants";

const { chartTitle, tooltip, baseId } =
  CHART_CONSTANTS.competitorNewProductDetectionsOverTime;

export function CompetitorNewProductDetectionsOverTime() {
  const [selectedPeriod, setSelectedPeriod] = useState("1");
  const chartRef = useRef<am4charts.XYChart>(null);

  const combinedFilters = useCombinedFilters();
  const competitorsProductIds = useWorkspaceStore(
    (state) => state.draft?.configuration.competitorsProductIds || []
  );
  const localizeInstalls = useWorkspaceStore(
    (state) => state.draft?.configuration?.localizeInstalls ?? true
  );

  const { marketTimeSeriesProductBreakdown, loading: isLoadingTrendingData } =
    useProductTrendingData({
      companyFilters: combinedFilters || {},
      ids: competitorsProductIds,
      lookBackYears: Number.parseInt(selectedPeriod),
      localizeInstalls,
    });

  const { productsNameMapById } = useMatchProductsById(competitorsProductIds);

  const chartData = useMemo(
    () =>
      convertProductTimeSeriesData(
        getTop5Products(marketTimeSeriesProductBreakdown),
        productsNameMapById
      ),
    [marketTimeSeriesProductBreakdown, productsNameMapById]
  );

  const noDataReturned = useMemo(
    () => hasNoData(isLoadingTrendingData, chartData),
    [chartData, isLoadingTrendingData]
  );

  const csvFieldMappings = useMemo(() => {
    const mappings: Record<string, string> = {
      date: "Date",
    };

    if (
      !chartData ||
      isLoadingTrendingData ||
      chartData.length === 0 ||
      !chartData[0].series
    ) {
      return mappings;
    }

    const [firstDataPoint] = chartData;
    firstDataPoint.series.forEach((seriesItem) => {
      const baseKey = seriesItem.name.toLowerCase().replace(/[\s&]+/g, "_");
      mappings[`series_${baseKey}`] = `${seriesItem.name} (# of companies)`;
    });

    return mappings;
  }, [chartData, isLoadingTrendingData]);

  const chartContent = useMemo(() => {
    return determineChartContent({
      productIds: competitorsProductIds,
      isLoading: isLoadingTrendingData,
      noDataReturned,
      baseId,
      chartToRender: (
        <BaseDateDataChart
          id={`${baseId}-chart`}
          chartData={chartData}
          baseColor={LineChartColors}
          yAxisLabel="New Product Detections"
          ref={chartRef}
        />
      ),
    });
  }, [chartData, isLoadingTrendingData, noDataReturned, competitorsProductIds]);

  return (
    <ChartWrapper
      id={`${baseId}-chart-wrapper`}
      title={chartTitle}
      tooltipTitle={tooltip}
      chart={chartContent}
      chartRef={chartRef}
      noData={noDataReturned || competitorsProductIds.length === 0}
      isLoading={isLoadingTrendingData}
      titleExtras={
        <PeriodSelect
          id={`${baseId}-period-select`}
          selectedPeriod={selectedPeriod}
          onChange={setSelectedPeriod}
        />
      }
      csvFieldMappings={csvFieldMappings}
    />
  );
}
